import { useCallback } from 'react';
import {
  GoogleMapsApiType,
  GoogleMapsUsageEventInput
} from './GoogleMapsUsageEvent';
import { googleMapsUsageTracker } from './GoogleMapsUsageTracker';

// Binds a channelGroupId to the shared tracker so callers of the generic
// Google Maps hooks (useGeocoder / usePlacesService / useMapSearch) can pass a
// small `onUsage` callback per apiType without the hooks knowing about
// channelGroupId at all. Records are dropped when channelGroupId is undefined
// so downstream aggregation stays clean.

type EventInputWithoutChannel = Omit<
  GoogleMapsUsageEventInput,
  'channelGroupId'
>;

export const useGoogleMapsUsage = (channelGroupId: number | undefined) => {
  const record = useCallback(
    (event: EventInputWithoutChannel) => {
      if (channelGroupId === undefined) return;
      googleMapsUsageTracker.record({
        ...event,
        channelGroupId
      } as GoogleMapsUsageEventInput);
    },
    [channelGroupId]
  );

  return {
    recordGeocoding: useCallback(
      (metadata: { hasResult: boolean }) => {
        record({ apiType: GoogleMapsApiType.GEOCODING, metadata });
      },
      [record]
    ),
    recordPlaces: useCallback(
      (metadata: {
        operation: 'searchByText' | 'searchNearby' | 'fetchFields';
        queryLength?: number;
        resultCount: number;
        radius?: number;
        sessionId?: string;
        fields?: string[];
      }) => {
        record({ apiType: GoogleMapsApiType.PLACES, metadata });
      },
      [record]
    ),
    recordAutocomplete: useCallback(
      (metadata: {
        queryLength: number;
        suggestionCount: number;
        sessionId?: string;
      }) => {
        record({ apiType: GoogleMapsApiType.AUTOCOMPLETE, metadata });
      },
      [record]
    ),
    recordFeatureLayer: useCallback(
      (metadata: { featureType: string }) => {
        record({ apiType: GoogleMapsApiType.FEATURE_LAYER, metadata });
      },
      [record]
    ),
    flush: useCallback(() => googleMapsUsageTracker.flush(), [])
  };
};
